import {
  MAX_POOL_SIZE,
  MEMORY_BUDGET_RATIO,
  MIN_POOL_SIZE,
  WORKER_PEAK_MEMORY_MB,
} from '../model/consts';

/**
 * 디바이스 메모리와 CPU 코어 수를 기반으로 압축 워커 풀 사이즈를 결정한다.
 *
 * 계산 로직:
 *   1. navigator.deviceMemory (GB) × MEMORY_BUDGET_RATIO → 이미지 압축에 쓸 수 있는 메모리 예산
 *   2. 예산 ÷ WORKER_PEAK_MEMORY_MB → 메모리 기준 최대 워커 수
 *   3. navigator.hardwareConcurrency - 1 → CPU 기준 최대 워커 수 (메인 스레드 1개 남김)
 *   4. 둘 중 작은 값을 택한 뒤 MIN/MAX 클램핑
 *
 * navigator.deviceMemory 미지원 브라우저(Firefox 등)는 4GB로 보수적 추정.
 *
 * 예시 (WORKER_PEAK_MEMORY_MB=90, MEMORY_BUDGET_RATIO=0.5):
 *   | Device RAM | CPU | 메모리 한도 | CPU 한도 | 결과 |
 *   |   2 GB     |  4  |     1       |    3     |   1  |
 *   |   4 GB     |  4  |     2       |    3     |   2  |
 *   |   8 GB     |  8  |     4       |    7     |   4  |
 *   |  16 GB     | 10  |     6(cap)  |    9     |   6  |
 */
export function calculatePoolSize(): number {
  const deviceMemoryGb =
    (navigator as NavigatorWithDeviceMemory).deviceMemory ?? 4;
  const cpuCores = navigator.hardwareConcurrency ?? 4;

  const memoryBudgetMb = deviceMemoryGb * 1024 * MEMORY_BUDGET_RATIO;
  const byMemory = Math.floor(memoryBudgetMb / WORKER_PEAK_MEMORY_MB);
  const byCpu = Math.max(1, cpuCores - 1);

  return Math.min(
    Math.max(Math.min(byMemory, byCpu), MIN_POOL_SIZE),
    MAX_POOL_SIZE,
  );
}

interface NavigatorWithDeviceMemory extends Navigator {
  /** @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/deviceMemory */
  deviceMemory?: number;
}
